import React, { useContext, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { ThemeProvider } from 'emotion-theming';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { ThemeContext } from '@ordermentum/ui-shared/utilities/theme';
import { Input } from '@ordermentum/ui-shared/atoms/Input';
import {
  SignedOutContainer,
  ButtonWrapper,
} from '@ordermentum/ui-shared/templates/SignupFlowTemplate/styles';
import Banner from '@ordermentum/ui-shared/components/Banner';
import Button from '@ordermentum/ui-shared/components/Button/Button';
import RightSideContent from '@ordermentum/ui-shared/templates/SignupFlowTemplate/components/RightSideContent';
import Spinner from '@ordermentum/ui-shared/foundations/Spinner';
import Select from '@ordermentum/ui-shared/components/Input/Select';
import { SupplierDirectoryType } from '@ordermentum/ui-types';
import {
  loadGoogleMaps,
  initialiseMaps,
  destroyGoogleMaps,
  unbindGoogleMaps,
} from '@ordermentum/ui-shared/utilities/google_maps_autocomplete';
import { AUS_STATES } from '@ordermentum/ui-constants';
import {
  Content,
  ErrorContainer,
  InputContainer,
  InputRow,
  InputRowField,
  getTheme,
} from './styles';
import { Pages, Venue, SignUpValues } from '..';
import { Icon } from '@ordermentum/ui-shared/foundations';

type Props = {
  onPageChange: (page: Pages) => void;
  onFormValueChange: (formValues: SignUpValues) => void;
  formValues?: SignUpValues;
  supplier?: SupplierDirectoryType;
};

const VenueForm = (props: Props) => {
  const theme = getTheme(useContext(ThemeContext));
  const { onPageChange, onFormValueChange, formValues, supplier } = props;
  const [formError, setFormError] = useState<string>('');
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [formPart, setFormPart] = useState<number>(1);
  const [submitForm, setSubmitForm] = useState<boolean>(false);

  const autocompleteInputRef = useRef<HTMLInputElement>(null);

  const initialValues = {
    tradingName: '',
    legalName: '',
    abn: '',
    venuePhone: '',
    street: '',
    suburb: '',
    postcode: '',
    state: '',
    country: supplier?.address?.country ?? 'Australia',
  };

  const [initialFormValues, setInitialFormValues] =
    useState<Venue>(initialValues);

  const onSubmit = async (values: Venue) => {
    if (submitForm) {
      setFormLoading(true);

      const registrationSuccessDestination = `${globalThis?.location.pathname}${globalThis?.location.search}${globalThis?.location.hash}`;
      const data = {
        first_name: formValues?.firstName,
        last_name: formValues?.lastName,
        email: formValues?.email,
        phone: '',
        destination: registrationSuccessDestination,
        password: formValues?.password,
        venueData: {
          ...values,
        },
      };

      try {
        await axios.post('/v1/register', data);
      } catch (err) {
        setFormLoading(false);
        setFormError(err.response.data.message);
        return;
      }

      setFormLoading(false);
      onPageChange('verifyEmail');
    }
  };

  const validationSchema = yup.object().shape({
    tradingName: yup.string().required(),
    legalName: yup.string().required(),
    abn: yup.string().required(),
    venuePhone: yup.string().required(),
    street: yup.string().required(),
    suburb: yup.string().required(),
    postcode: yup.string().required(),
    state: yup.string().required(),
    country: yup.string().nullable(),
  });

  const showPartTwo = () => {
    setFormPart(2);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema,
    onSubmit,
    validateOnMount: true,
  });

  let places;
  const fillInAddress = () => {
    const place = places?.getPlace();
    if (!place) {
      return;
    }
    let address = '';
    for (let i = 0; i < place.address_components.length; i += 1) {
      const addressType = place.address_components[i].types[0];
      switch (addressType) {
        case 'subpremise':
          address = `${place.address_components[i].short_name}/${address}`;
          break;
        case 'street_number':
          address = `${address + place.address_components[i].short_name} `;
          break;
        case 'route':
          address += place.address_components[i].long_name;
          break;
        case 'locality':
          formik.setFieldValue(
            'suburb',
            place.address_components[i].long_name,
            false
          );
          break;
        case 'administrative_area_level_1':
          formik.setFieldValue(
            'state',
            place.address_components[i].short_name,
            false
          );
          break;
        case 'postal_code':
          formik.setFieldValue(
            'postcode',
            place.address_components[i].short_name,
            false
          );
          break;
        default:
          break;
      }
    }
    formik.setFieldValue('street', address);
    formik.setErrors({});
  };

  useEffect(() => {
    if (formValues) {
      setInitialFormValues(formValues);
    } else {
      setInitialFormValues(initialValues);
    }
  }, [formValues]);

  useEffect(() => {
    loadGoogleMaps();
    return () => {
      destroyGoogleMaps();
    };
  }, []);

  useEffect(() => {
    if (formPart === 2 && autocompleteInputRef.current) {
      initialiseMaps(autocompleteInputRef, fillInAddress)
        .then(maps => {
          places = maps;
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.error('Failed to initialise google maps');
        });
    } else {
      unbindGoogleMaps();
    }
  }, [formPart, autocompleteInputRef.current]);

  return (
    <ThemeProvider theme={theme}>
      <RightSideContent
        heading="Enter details of your venue that will trade with this supplier"
        subheading={`Step ${formPart === 1 ? '2' : '3'} / 3`}
        footerActions={
          <>
            <SignedOutContainer>
              <Button
                type="button"
                onClick={() => {
                  if (formPart === 2) {
                    setFormPart(1);
                  } else {
                    onPageChange('signUp');
                    if (formValues) {
                      const data = {
                        ...formValues,
                        ...formik.values,
                      };
                      onFormValueChange(data);
                    }
                  }
                }}
                icon={<Icon name="sChevronLeft" inheritFill />}
                variant="tertiary"
              >
                Back
              </Button>
            </SignedOutContainer>

            <ButtonWrapper>
              {formPart === 1 ? (
                <Button
                  type="submit"
                  variant="primary"
                  form="venue-form"
                  onClick={e => {
                    const { tradingName, legalName, abn, venuePhone } =
                      formik.errors;
                    const formPartOneErrors =
                      tradingName || legalName || abn || venuePhone;

                    if (!formPartOneErrors) {
                      showPartTwo();
                      e.currentTarget.blur();
                      e.preventDefault();
                    }
                  }}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isFormLoading}
                  variant="primary"
                  form="venue-form"
                  // should only submit the values to the api
                  // if user reaches part 2 of the form
                  // otherwise do validations
                  onClick={() => setSubmitForm(true)}
                >
                  {isFormLoading ? (
                    <Spinner size="S" color="white" />
                  ) : (
                    'Continue'
                  )}
                </Button>
              )}
            </ButtonWrapper>
          </>
        }
      >
        <Content>
          <form
            id="venue-form"
            onSubmit={formik.handleSubmit}
            autoComplete="off"
          >
            <InputContainer>
              {formPart === 1 ? (
                <>
                  <Input
                    inputLabel="Venue Trading Name"
                    size="max"
                    tag="tradingName"
                    type="text"
                    value={formik.values.tradingName}
                    onChange={formik.setFieldValue}
                    required
                  />
                  <Input
                    inputLabel="Legal Name / Corporate Entity"
                    size="max"
                    tag="legalName"
                    type="text"
                    value={formik.values.legalName}
                    onChange={formik.setFieldValue}
                    required
                  />
                  <Input
                    inputLabel="ABN"
                    size="max"
                    tag="abn"
                    type="text"
                    value={formik.values.abn}
                    onChange={formik.setFieldValue}
                    required
                  />
                  <Input
                    inputLabel="Phone"
                    size="max"
                    tag="venuePhone"
                    type="text"
                    value={formik.values.venuePhone}
                    onChange={formik.setFieldValue}
                    required
                  />
                </>
              ) : (
                <>
                  <Input
                    inputLabel="Street Address"
                    size="max"
                    tag="street"
                    type="text"
                    value={formik.values.street}
                    onChange={formik.setFieldValue}
                    required
                    innerRef={autocompleteInputRef}
                  />
                  <Input
                    inputLabel="Suburb"
                    size="max"
                    tag="suburb"
                    type="text"
                    value={formik.values.suburb}
                    onChange={formik.setFieldValue}
                    required
                  />

                  <InputRow>
                    <InputRowField>
                      <Input
                        inputLabel="Postcode"
                        size="max"
                        tag="postcode"
                        type="text"
                        value={formik.values.postcode}
                        onChange={formik.setFieldValue}
                        required
                      />
                    </InputRowField>

                    <InputRowField>
                      <Select
                        label="State"
                        name="state"
                        placeholder="Select"
                        selectedValue={formik.values.state ?? ''}
                        options={[
                          ...AUS_STATES.map(state => ({
                            name: state,
                            key: state,
                          })),
                        ]}
                        onChange={formik.setFieldValue}
                        size="small"
                        noPadding={false}
                        overflow
                        overlayHeight="12.5rem"
                      />
                    </InputRowField>
                  </InputRow>
                </>
              )}
            </InputContainer>

            {!!formError && (
              <ErrorContainer>
                <Banner variant="error">{formError}</Banner>
              </ErrorContainer>
            )}
          </form>
        </Content>
      </RightSideContent>
    </ThemeProvider>
  );
};

export default VenueForm;
